import Map from 'ol/Map';
import {
  DIGISAKA_API_CROWN_SEGMENTATION,
  DIGISAKA_API_TREEDETECTION,
  digisakaAuthRequestInit,
  gptInferenceJsonHeaders,
} from '../../../config/digisakaGptInference';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import { deriveViewportResolutionMeters } from './mapDisplayScale';

export type TreeDetectViewportParams = {
  tile_size: number;
  overlap: number;
  resolution: number;
  conf_threshold: number;
};

export type CrownSegmentationViewportParams = {
  tile_size: number;
  overlap: number;
  resolution: number;
  conf_threshold: number;
  sam_model_type: string;
  device: string;
};

/**
 * POST ``/api/treedetection`` (JSON) — server builds GeoTIFF from viewport PNG + EPSG:3857
 * extent and runs the same model as file upload.
 */
export async function treeDetectFromViewport(
  map: Map,
  params: TreeDetectViewportParams,
  signal?: AbortSignal,
): Promise<unknown> {
  const view = map.getView();
  const size = map.getSize();
  if (!size || view.getCenter() === undefined) {
    throw new Error('Map view is not ready. Pan or zoom once and try again.');
  }

  await new Promise<void>((resolve) => {
    map.once('rendercomplete', () => resolve());
    map.renderSync();
  });

  const canvas = map.getViewport().querySelector('canvas') as HTMLCanvasElement | null;
  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error('Could not read map canvas.');
  }

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    throw new Error(
      'Canvas is tainted (likely due to tile CORS). Try the OpenStreetMap base layer or another source that allows export.'
    );
  }

  const extent3857 = view.calculateExtent(size) as [number, number, number, number];
  if (signal?.aborted) {
    throw new DOMException('Analysis cancelled', 'AbortError');
  }

  const resolution = deriveViewportResolutionMeters(map, params.resolution);

  const res = await fetch(
    DIGISAKA_API_TREEDETECTION,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: gptInferenceJsonHeaders(),
      signal,
      body: JSON.stringify({
        png_base64: dataUrl,
        width: canvas.width,
        height: canvas.height,
        extent_3857: extent3857,
        tile_size: params.tile_size,
        overlap: params.overlap,
        resolution,
        conf_threshold: params.conf_threshold,
      }),
    })
  );

  notifyInferenceComputeUsage(res);

  if (!res.ok) {
    let msg = `API error: ${res.status}`;
    try {
      const errBody = await res.json();
      if (errBody?.detail) {
        msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
      }
    } catch {
      const t = await res.text();
      if (t) msg = t;
    }
    throw new Error(msg);
  }

  return res.json();
}

/**
 * POST ``/api/crown-segmentation`` (JSON) — server builds GeoTIFF from viewport PNG +
 * EPSG:3857 extent and runs the same pipeline as file upload.
 */
export async function crownSegmentationFromViewport(
  map: Map,
  params: CrownSegmentationViewportParams,
  signal?: AbortSignal,
): Promise<unknown> {
  const view = map.getView();
  const size = map.getSize();
  if (!size || view.getCenter() === undefined) {
    throw new Error('Map view is not ready. Pan or zoom once and try again.');
  }

  await new Promise<void>((resolve) => {
    map.once('rendercomplete', () => resolve());
    map.renderSync();
  });

  const canvas = map.getViewport().querySelector('canvas') as HTMLCanvasElement | null;
  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error('Could not read map canvas.');
  }

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    throw new Error(
      'Canvas is tainted (likely due to tile CORS). Try the OpenStreetMap base layer or another source that allows export.'
    );
  }

  const extent3857 = view.calculateExtent(size) as [number, number, number, number];
  if (signal?.aborted) {
    throw new DOMException('Analysis cancelled', 'AbortError');
  }

  const resolution = deriveViewportResolutionMeters(map, params.resolution);

  const res = await fetch(
    DIGISAKA_API_CROWN_SEGMENTATION,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: gptInferenceJsonHeaders(),
      signal,
      body: JSON.stringify({
        png_base64: dataUrl,
        width: canvas.width,
        height: canvas.height,
        extent_3857: extent3857,
        tile_size: params.tile_size,
        overlap: params.overlap,
        resolution,
        conf_threshold: params.conf_threshold,
        sam_model_type: params.sam_model_type,
        device: params.device,
      }),
    })
  );

  notifyInferenceComputeUsage(res);

  if (!res.ok) {
    let msg = `API error: ${res.status}`;
    try {
      const errBody = await res.json();
      if (errBody?.detail) {
        msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
      }
    } catch {
      const t = await res.text();
      if (t) msg = t;
    }
    throw new Error(msg);
  }

  return res.json();
}
